"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCart } from "@/context/CartContext";
import { useOutlet } from "@/context/OutletContext";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, ArrowRight } from "lucide-react";

const CartBar = () => {
  const { totalItems, totalPrice } = useCart();
  const { displayAddress } = useOutlet();
  const pathname = usePathname();

  if (pathname === "/cart") return null;

  return (
    <AnimatePresence>
      {totalItems > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-20 md:bottom-6 left-0 right-0 z-50 px-4 pointer-events-none"
        >
          <div className="max-w-3xl mx-auto pointer-events-auto">
            <Link
              href="/cart"
              className="flex items-center justify-between bg-white/70 backdrop-blur-xl border border-white/40 rounded-[28px] p-2 md:p-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.1)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] transition-all duration-300 group overflow-hidden"
            >
              <div className="flex items-center gap-3 md:gap-4 pl-1.5">
                {/* Left Icon */}
                <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-[#F8F2F2] flex items-center justify-center flex-shrink-0 border border-black/5">
                  <div className="relative">
                    <ShoppingBag className="w-4 h-4 md:w-6 md:h-6 text-[#a71010]" strokeWidth={2.5} />
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border-2 border-[#F8F2F2]" />
                  </div>
                </div>

                {/* Info Text */}
                <div className="flex flex-col">
                  <div className="flex flex-wrap items-center gap-1 md:gap-2">
                    <span className="font-bold text-[#1A1A1A] text-[13px] md:text-[15px]">{displayAddress}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="text-gray-500 text-[11px] md:text-sm font-medium">{totalItems} items</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="font-bold text-[#1A1A1A] text-[13px] md:text-[15px]">₹{totalPrice.toFixed(0)}</span>
                  </div>
                </div>
              </div>

              {/* View Cart Button */}
              <div className="flex items-center gap-2 bg-[#a71010] hover:bg-[#810606] text-white pl-4 pr-3 md:pl-6 md:pr-5 py-2.5 md:py-3.5 rounded-full font-bold text-sm md:text-[15px] transition-all duration-300 shadow-md transform group-active:scale-95 leading-none">
                <span className="hidden xs:inline">View Cart</span>
                <span className="xs:hidden">Cart</span>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-1 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CartBar;
